import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import toast from 'react-hot-toast';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import MultiDayDateRangeFields, {
  getDefaultMultiDayRange,
  MAX_MULTI_DAY_RANGE,
} from '../../components/MultiDayDateRangeFields';
import { resolveMultiDayDates } from '../../utils/dateHelpers';
import {
  getTimeSpentValidationError,
  TIME_SPENT_INCREMENT_MINUTES,
} from '../../utils/timeEntryTimeSpent';

interface Customer {
  id: string;
  accountName: string;
}

interface Engagement {
  id: string;
  name: string;
  number: string;
}

interface Group {
  id: string;
  name: string;
}

interface TypeOption {
  value: string;
  label: string;
}

interface FieldErrors {
  customerId?: string;
  engagementId?: string;
  type?: string;
  timeSpent?: string;
  workedDate?: string;
  groupId?: string;
  description?: string;
  multiDay?: string;
}

export default function TimeEntryCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const timeEntriesListSearch = (location.state as { timeEntriesListSearch?: string } | null)?.timeEntriesListSearch;
  const timeEntriesListPath =
    timeEntriesListSearch !== undefined ? `/time-entries${timeEntriesListSearch}` : '/time-entries';
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { canCreateEntity, loading: permissionsLoading } = useUserPermissions();
  const { preferences, getToday } = useDateFormatter();
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);

  // Get today's date in user's preferred timezone
  const todayInUserTimezone = getToday();

  const [formData, setFormData] = useState({
    customerId: '',
    engagementId: '',
    groupId: '',
    type: '',
    timeSpent: '',
    workedDate: '',
    description: ''
  });

  // Set initial worked date based on user's timezone preference
  // Also adjust if selected date exceeds max date after timezone change
  useEffect(() => {
    if (todayInUserTimezone) {
      if (!formData.workedDate) {
        // Set initial date to today in user's timezone
        setFormData(prev => ({ ...prev, workedDate: todayInUserTimezone }));
      } else if (formData.workedDate > todayInUserTimezone) {
        // If selected date exceeds max date (e.g., after timezone change), adjust to max
        setFormData(prev => ({ ...prev, workedDate: todayInUserTimezone }));
      }
    }
  }, [todayInUserTimezone]);

  const [typeQuery, setTypeQuery] = useState('');
  const [addMultipleDays, setAddMultipleDays] = useState(false);
  const [rangeStartDate, setRangeStartDate] = useState('');
  const [rangeEndDate, setRangeEndDate] = useState('');
  const [includeWeekends, setIncludeWeekends] = useState(false);

  const resolvedDates = useMemo(
    () =>
      addMultipleDays
        ? resolveMultiDayDates({
            rangeStartDate,
            rangeEndDate,
            maxDate: todayInUserTimezone,
            includeWeekends,
          })
        : [],
    [addMultipleDays, rangeStartDate, rangeEndDate, todayInUserTimezone, includeWeekends]
  );

  const canCreate = canCreateEntity('time_entries');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData.customerId) {
      fetchEngagements(formData.customerId);
      // Clear customer error when selected
      if (fieldErrors.customerId) {
        setFieldErrors(prev => ({ ...prev, customerId: undefined }));
      }
    } else {
      setEngagements([]);
      setFormData(prev => ({ ...prev, engagementId: '', groupId: '' }));
    }
  }, [formData.customerId]);

  useEffect(() => {
    if (formData.engagementId) {
      fetchUserGroups(formData.engagementId);
      fetchTypeOptions(formData.engagementId);
      setFormData((prev) => ({ ...prev, groupId: '' }));
      // Clear engagement error when selected
      if (fieldErrors.engagementId) {
        setFieldErrors(prev => ({ ...prev, engagementId: undefined }));
      }
    } else {
      setGroups([]);
      setTypeOptions([]);
      setFormData(prev => ({ ...prev, groupId: '', type: '' }));
    }
  }, [formData.engagementId]);

  const fetchInitialData = async () => {
    try {
      const customersRes = await api.get('/customers?limit=1000&context=time_entry');
      setCustomers(customersRes.data.customers || customersRes.data || []);
      // Don't fetch types initially - will be fetched when engagement is selected
      setTypeOptions([]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchEngagements = async (customerId: string) => {
    try {
      const response = await api.get(`/engagements?customerId=${customerId}&limit=1000`);
      setEngagements(response.data.engagements || response.data || []);
    } catch (error) {
      console.error('Error fetching engagements:', error);
      setEngagements([]);
    }
  };

  const fetchUserGroups = async (engagementId: string) => {
    try {
      const response = await api.get(`/time-entries/user-groups?engagementId=${engagementId}`);
      setGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
      setGroups([]);
    }
  };

  const fetchTypeOptions = async (engagementId: string) => {
    try {
      const response = await api.get(`/time-entries/types?engagementId=${engagementId}`);
      setTypeOptions(response.data || []);
      // Clear type selection when engagement changes
      setFormData(prev => ({ ...prev, type: '' }));
    } catch (error) {
      console.error('Error fetching type options:', error);
      toast.error('Failed to load time entry types');
      setTypeOptions([]);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const filteredTypes = typeQuery === ''
    ? typeOptions
    : typeOptions.filter((option) =>
        option.label.toLowerCase().includes(typeQuery.toLowerCase())
      );

  const selectedType = typeOptions.find(t => t.value === formData.type);

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    if (!formData.type) {
      errors.type = 'Type is required';
    }
    const timeSpentError = getTimeSpentValidationError(formData.timeSpent);
    if (timeSpentError) {
      errors.timeSpent = timeSpentError;
    }
    if (!addMultipleDays) {
      if (!formData.workedDate) {
        errors.workedDate = 'Worked Date is required';
      } else if (todayInUserTimezone && formData.workedDate > todayInUserTimezone) {
        errors.workedDate = 'Worked Date cannot be in the future';
      }
    } else {
      if (!rangeStartDate || !rangeEndDate) {
        errors.multiDay = 'Select a start date and end date.';
      } else if (rangeStartDate > rangeEndDate) {
        errors.multiDay = 'End date must be on or after the start date.';
      } else if (todayInUserTimezone && rangeEndDate > todayInUserTimezone) {
        errors.multiDay = 'End date cannot be after today.';
      } else if (resolvedDates.length === 0) {
        errors.multiDay = includeWeekends
          ? 'No eligible dates in the selected range.'
          : 'No weekdays in the selected range. Adjust the range or enable "Add Entries for Weekend".';
      } else if (resolvedDates.length > MAX_MULTI_DAY_RANGE) {
        errors.multiDay = `You can create at most ${MAX_MULTI_DAY_RANGE} entries at a time.`;
      }
    }
    if (!formData.groupId) {
      errors.groupId = 'Group is required';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (addMultipleDays) {
        const response = await api.post('/time-entries/bulk', {
          customerId: formData.customerId,
          engagementId: formData.engagementId,
          groupId: formData.groupId,
          type: formData.type,
          timeSpent: parseInt(formData.timeSpent, 10),
          description: formData.description,
          dates: resolvedDates,
        });
        const createdCount = response.data?.created || resolvedDates.length;
        toast.success(`Created ${createdCount} time entr${createdCount === 1 ? 'y' : 'ies'} successfully`);
      } else {
        await api.post('/time-entries', {
          ...formData,
          timeSpent: parseInt(formData.timeSpent, 10),
        });
        toast.success('Time entry recorded successfully');
      }
      // Invalidate all time entry queries to refresh the list (including hierarchy view)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['timeEntries'] }),
        queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] }),
        queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] }),
      ]);
      navigate(timeEntriesListPath);
    } catch (error: any) {
      console.error('Error creating time entry:', error);
      const errorMessage = error.response?.data?.message || 'Failed to record time entry';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!canCreate) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to record time.</p>
        </div>
      </div>
    );
  }

  const saveButtonLabel =
    saving
      ? 'Saving...'
      : addMultipleDays && resolvedDates.length > 0
        ? `Save ${resolvedDates.length} Entries`
        : 'Save';

  const formActions = (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        {saveButtonLabel}
      </button>
      <Link
        to={timeEntriesListPath}
        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
      >
        Cancel
      </Link>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to={timeEntriesListPath}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Time Entries
        </Link>

        <div className="flex justify-between items-start">
          <h1 className="text-2xl font-bold text-gray-900">Record Time</h1>

          {formActions}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Row 1: User and Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">User</label>
              <div className="col-span-2">
                <input
                  type="text"
                  value={`${user?.firstName || ''} ${user?.lastName || ''}`}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Customer <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <select
                  value={formData.customerId}
                  onChange={(e) => handleInputChange('customerId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    fieldErrors.customerId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.accountName}
                    </option>
                  ))}
                </select>
                {fieldErrors.customerId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.customerId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Engagement and Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Engagement <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <select
                  value={formData.engagementId}
                  onChange={(e) => handleInputChange('engagementId', e.target.value)}
                  disabled={!formData.customerId}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    fieldErrors.engagementId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Engagement</option>
                  {engagements.map((engagement) => (
                    <option key={engagement.id} value={engagement.id}>
                      {engagement.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.engagementId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.engagementId}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <Combobox
                  value={formData.type}
                  onChange={(value) => handleInputChange('type', value)}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                          fieldErrors.type ? 'border-red-500' : 'border-gray-300'
                        }`}
                        displayValue={() => selectedType?.label || ''}
                        onChange={(event) => setTypeQuery(event.target.value)}
                        placeholder="Select or search type..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setTypeQuery('')}
                    >
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredTypes.length === 0 && typeQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            Nothing found.
                          </div>
                        ) : (
                          filteredTypes.map((option) => (
                            <Combobox.Option
                              key={option.value}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                }`
                              }
                              value={option.value}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {option.label}
                                  </span>
                                  {selected ? (
                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </div>
                </Combobox>
                {fieldErrors.type && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.type}</p>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Time Spent and Group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Time Spent (In Mins) <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <input
                  type="number"
                  min={TIME_SPENT_INCREMENT_MINUTES}
                  step={TIME_SPENT_INCREMENT_MINUTES}
                  value={formData.timeSpent}
                  onChange={(e) => handleInputChange('timeSpent', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    fieldErrors.timeSpent ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g. 15, 30, 45, 60"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter time in 15-minute increments only
                </p>
                {fieldErrors.timeSpent && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.timeSpent}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Group <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <select
                  value={formData.groupId}
                  onChange={(e) => handleInputChange('groupId', e.target.value)}
                  disabled={!formData.engagementId}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    fieldErrors.groupId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.groupId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.groupId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Add for multiple days */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-sm font-medium text-gray-700 pt-2">
                Add for multiple days
              </label>
              <div className="col-span-2 pt-2">
                <input
                  type="checkbox"
                  checked={addMultipleDays}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAddMultipleDays(checked);
                    if (checked && todayInUserTimezone) {
                      const defaults = getDefaultMultiDayRange(todayInUserTimezone);
                      setRangeStartDate(defaults.start);
                      setRangeEndDate(defaults.end);
                      setIncludeWeekends(false);
                    }
                    if (fieldErrors.multiDay || fieldErrors.workedDate) {
                      setFieldErrors((prev) => ({ ...prev, multiDay: undefined, workedDate: undefined }));
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Worked Date — below time spent row */}
          {!addMultipleDays && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="grid grid-cols-3 gap-4 items-start">
                <label className="text-sm font-medium text-gray-700 pt-2">
                  Worked Date <span className="text-red-500">*</span>
                </label>
                <div className="col-span-2">
                  <DateTimePickerInput
                    value={formData.workedDate}
                    onChange={(value) => handleInputChange('workedDate', value)}
                    max={todayInUserTimezone}
                    showTimeSelect={false}
                    error={!!fieldErrors.workedDate}
                    placeholder="Select date"
                  />
                  {fieldErrors.workedDate && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.workedDate}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Multi-day date range — full width (12-column layout) */}
          {addMultipleDays && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-4 items-start">
              <label className="text-sm font-medium text-gray-700 md:col-span-2 pt-2">
                Worked Date Range <span className="text-red-500">*</span>
              </label>
              <div className="md:col-span-10">
                <div className="rounded-lg border border-indigo-100 bg-white p-5 shadow-sm">
                  <MultiDayDateRangeFields
                    layout="wide"
                    rangeStartDate={rangeStartDate}
                    rangeEndDate={rangeEndDate}
                    includeWeekends={includeWeekends}
                    onRangeStartChange={(value) => {
                      setRangeStartDate(value);
                      if (fieldErrors.multiDay) {
                        setFieldErrors((prev) => ({ ...prev, multiDay: undefined }));
                      }
                    }}
                    onRangeEndChange={(value) => {
                      setRangeEndDate(value);
                      if (fieldErrors.multiDay) {
                        setFieldErrors((prev) => ({ ...prev, multiDay: undefined }));
                      }
                    }}
                    onIncludeWeekendsChange={(checked) => {
                      setIncludeWeekends(checked);
                      if (fieldErrors.multiDay) {
                        setFieldErrors((prev) => ({ ...prev, multiDay: undefined }));
                      }
                    }}
                    maxDate={todayInUserTimezone}
                    error={fieldErrors.multiDay}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Row 5: Description (full width) */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-start">
            <label className="text-sm font-medium text-gray-700 md:col-span-1 pt-2">
              Description <span className="text-red-500">*</span>
            </label>
            <div className="md:col-span-5">
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  fieldErrors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter description of work performed"
              />
              {fieldErrors.description && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.description}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
            {formActions}
          </div>
        </div>
      </div>
    </div>
  );
}
